
import { Transaction } from "@/services/salesService";
import { supabase } from "@/integrations/supabase/client";
import { SUPABASE_PROJECT_URL, SUPABASE_ANON_KEY } from '@/config/supabase';
import { toast } from "@/components/ui/use-toast";
import { DateRange } from "react-day-picker";
import { debugLog, debugError } from '@/utils/debugConfig';

/**
 * Gets date parameters from a DateRange object
 */
const getDateParams = (dateRange: DateRange | undefined) => {
  if (!dateRange?.from) {
    return { startDate: null, endDate: null };
  }
  
  const startDate = dateRange.from.toISOString().split('T')[0];
  const endDate = dateRange.to ? dateRange.to.toISOString().split('T')[0] : startDate;
  
  return { startDate, endDate };
}

/**
 * Fetches transaction data with proper authentication
 */
export async function fetchTransactions(dateRange: DateRange | undefined): Promise<Transaction[]> {
  try {
    // Get current user session for authentication
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      throw new Error('No active session found');
    }
    
    // Get authenticated user ID
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User not authenticated');
    }
    
    const creator_id = user.id;
    const { startDate, endDate } = getDateParams(dateRange);
    
    debugLog('TRANSACTIONS', `Fetching transactions for creator ${creator_id} from ${startDate || 'all time'} to ${endDate || 'now'}`);
    
    // Use the Edge Function with proper headers
    const response = await fetch(`${SUPABASE_PROJECT_URL}/functions/v1/query-transactions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': SUPABASE_ANON_KEY
      },
      body: JSON.stringify({ 
        creator_id, 
        start_date: startDate, 
        end_date: endDate 
      })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      let errorData;
      
      // Try to parse the error response as JSON
      try {
        errorData = JSON.parse(errorText);
      } catch (e) {
        errorData = { error: errorText || `API returned ${response.status}` };
      }
      
      debugError('TRANSACTIONS', "API Error:", errorData);
      throw new Error(errorData.error || `API returned ${response.status}`);
    }
    
    const transactions = await response.json();
    debugLog('TRANSACTIONS', `Successfully fetched ${transactions?.length || 0} transactions`);
    
    return transactions.map((tx: any) => ({
      id: tx.id || `temp-${Math.random().toString(36).substring(7)}`,
      date: tx.date || new Date().toISOString(),
      course: tx.course || 'Unknown Course',
      student: tx.student || 'Unknown Student',
      amount: typeof tx.amount === 'number' ? tx.amount : parseFloat(tx.amount) || 0,
      status: tx.status as "completed" | "pending",
      stripe_invoice_url: tx.stripe_invoice_url || undefined
    }));
    
  } catch (error: any) {
    debugError('TRANSACTIONS', "Error fetching transactions:", error);
    toast({
      title: "Error loading transactions",
      description: error.message || "Could not retrieve transaction data. Please try again later.",
      variant: "destructive"
    });
    
    return [];
  }
}

/**
 * Export transactions to CSV
 */
export function exportTransactionsCSV(transactions: Transaction[]): void {
  if (!transactions.length) {
    toast({
      title: "No transactions to export",
      description: "There are no transactions in the selected period.",
      variant: "destructive"
    });
    return;
  }

  const headers = ["Date", "Course", "Student", "Amount", "Status"];
  const csvRows = [
    headers.join(","),
    ...transactions.map(t => {
      return [
        t.date.split('T')[0],
        `"${t.course.replace(/"/g, '""')}"`,
        `"${t.student.replace(/"/g, '""')}"`,
        t.amount,
        t.status
      ].join(",");
    })
  ];
  
  const csvContent = "data:text/csv;charset=utf-8," + csvRows.join("\n");
  
  const encodedUri = encodeURI(csvContent);
  const link = document.createElement("a");
  link.setAttribute("href", encodedUri);
  link.setAttribute("download", `transactions_${new Date().toISOString().split('T')[0]}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  toast({
    title: "Export successful",
    description: "Transaction data has been exported to CSV.",
  });
}
